import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './HeroDetectionMap.css';

/* ════════════════════════════════════════════════════════════════
   HERO DETECTION MAP
   A faithful React port of the Claude Design handoff
   ("Hero Detection Map.dc.html"). The isometric scene — tile lift,
   animated safe path, walking cane, hover tooltips — is reproduced
   exactly; only the surrounding layout has been adapted so the
   graphic integrates cleanly into the marketing homepage.
   ════════════════════════════════════════════════════════════════ */

/* ── Scene constants (verbatim from the handoff logic) ────────── */
const CFG = { COLS: 9, ROWS: 9, TW: 128, TH: 64, D: 18, ORX: 824, ORY: 300 };
const ACCENT = '#3b82f6';
const SAFE = '#34c759';

const OBSTACLE_LIST = [
  ['8,6', 'Raised kerb', '4 cm', 16, 'trip'],
  ['7,5', 'Raised kerb', '4 cm', 16, 'trip'],
  ['8,3', 'Cracked paver', '3 cm', 12, 'trip'],
  ['7,2', 'Raised kerb', '4 cm', 16, 'trip'],
  ['6,4', 'Step edge', '15 cm', 34, 'mid'],
  ['5,6', 'Fire hydrant', '0.6 m', 70, 'mid'],
  ['4,5', 'Litter bin', '0.7 m', 80, 'mid'],
  ['1,6', 'Bench end', '0.45 m', 56, 'mid'],
  ['3,5', 'Bollard', '0.9 m', 100, 'high'],
  ['4,1', 'Bollard', '0.9 m', 100, 'high'],
  ['2,4', 'Lamp pole', '1.1 m', 116, 'high'],
  ['0,5', 'Lamp pole', '1.1 m', 116, 'high'],
  ['1,3', 'Sign post', '2.0 m', 150, 'high'],
];
const OBSTACLES = {};
OBSTACLE_LIST.forEach((a) => { OBSTACLES[a[0]] = { name: a[1], measure: a[2], px: a[3], cat: a[4] }; });

const PATH = [[8, 8], [7, 8], [6, 7], [6, 6], [5, 5], [5, 4], [4, 3], [3, 3], [2, 2], [2, 1], [1, 1], [0, 0]];

const PAL = {
  ground: { top: '#1c2027', topHi: '#262c36', left: '#13161c', right: '#0d1015', stroke: 'rgba(255,255,255,0.05)', badge: SAFE },
  trip:   { top: '#33506f', topHi: '#41607f', left: '#223850', right: '#192a3d', stroke: 'rgba(127,176,230,0.55)', badge: '#8fbcf0' },
  mid:    { top: '#2f63a8', topHi: '#3c74bd', left: '#214a80', right: '#173860', stroke: 'rgba(96,165,250,0.65)', badge: '#82b6ff' },
  high:   { top: ACCENT, topHi: '#5b9bff', left: '#2b62c0', right: '#1f478e', stroke: 'rgba(147,197,253,0.92)', badge: '#aacdff' },
};
const CAT = { trip: 'LOW · TRIP HAZARD', mid: 'MID OBSTACLE', high: 'HIGH OBSTACLE' };

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const center = (c, r) => [CFG.ORX + (c - r) * CFG.TW / 2, CFG.ORY + (c + r) * CFG.TH / 2];
const diamond = (cx, cy) => `${cx},${cy - CFG.TH / 2} ${cx + CFG.TW / 2},${cy} ${cx},${cy + CFG.TH / 2} ${cx - CFG.TW / 2},${cy}`;
const leftF = (cx, topY, botY) => `${cx - CFG.TW / 2},${topY} ${cx},${topY + CFG.TH / 2} ${cx},${botY + CFG.TH / 2} ${cx - CFG.TW / 2},${botY}`;
const rightF = (cx, topY, botY) => `${cx},${topY + CFG.TH / 2} ${cx + CFG.TW / 2},${topY} ${cx + CFG.TW / 2},${botY} ${cx},${botY + CFG.TH / 2}`;

/* Pre-compute the safe-path geometry (independent of animation state). */
const PATH_PTS = PATH.map(([c, r]) => { const [x, y] = center(c, r); return [x, y - 2]; });
const DPATH = 'M' + PATH_PTS.map((p) => p.join(' ')).join(' L ');

/* ── The isometric SVG scene — graphic is identical everywhere ─── */
const DetectionScene = ({ elapsed, hover, setHover }) => {
  const { COLS, ROWS, D, TH } = CFG;

  // build + depth-sort tiles
  const tiles = [];
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      const key = c + ',' + r;
      const ob = OBSTACLES[key] || null;
      const [gx, gy] = center(c, r);
      let lift = 0, cat = 'ground';
      if (ob) {
        const delay = (c + r) * 55;
        lift = ob.px * easeOut(clamp((elapsed - delay) / 1500, 0, 1));
        cat = ob.cat;
      }
      tiles.push({ c, r, key, gx, gy, lift, ob, cat, dep: c + r });
    }
  }
  tiles.sort((a, b) => (a.dep - b.dep) || (a.c - b.c));

  const tileEls = tiles.map((t) => {
    const col = t.ob ? PAL[t.cat] : PAL.ground;
    const topY = t.gy - t.lift, botY = t.gy + D;
    const hovered = hover === t.key;
    const glow = t.ob && t.cat === 'high' && t.lift > 36;
    return (
      <g
        key={t.key}
        style={{ cursor: 'pointer', filter: glow ? 'drop-shadow(0 0 16px rgba(59,130,246,0.5))' : 'none', transition: 'filter .2s' }}
        onMouseEnter={() => setHover(t.key)}
        onMouseLeave={() => setHover((h) => (h === t.key ? null : h))}
      >
        <polygon points={leftF(t.gx, topY, botY)} fill={col.left} />
        <polygon points={rightF(t.gx, topY, botY)} fill={col.right} />
        <polygon
          points={diamond(t.gx, topY)}
          fill={hovered ? col.topHi : col.top}
          stroke={hovered ? '#bfdbfe' : col.stroke}
          strokeWidth={hovered ? 2 : 1}
          strokeLinejoin="round"
        />
      </g>
    );
  });

  // tooltip
  let tip = null;
  const ht = hover && tiles.find((x) => x.key === hover);
  if (ht) {
    const info = ht.ob
      ? { title: ht.ob.name, measure: ht.ob.measure, cat: CAT[ht.cat], col: PAL[ht.cat].badge }
      : { title: 'Clear surface', measure: 'level', cat: 'SAFE TO TRAVERSE', col: SAFE };
    const w = Math.max(170, info.title.length * 11 + 70);
    const tx = ht.gx, ty = ht.gy - ht.lift - TH / 2;
    const gap = 26, hgt = 80, top = -gap - hgt;
    tip = (
      <g style={{ pointerEvents: 'none' }} transform={`translate(${tx} ${ty})`}>
        <line x1={0} y1={0} x2={0} y2={-gap} stroke={info.col} strokeWidth={1.5} opacity={0.7} />
        <circle cx={0} cy={0} r={3} fill={info.col} />
        <rect x={-w / 2} y={top} width={w} height={hgt} rx={14} fill="rgba(13,15,20,0.97)" stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
        <rect x={-w / 2} y={top} width={3} height={hgt} rx={1.5} fill={info.col} />
        <text x={0} y={top + 22} textAnchor="middle" fill={info.col} style={{ font: '600 10px var(--cw-font-body)', letterSpacing: '0.16em' }}>{info.cat}</text>
        <text x={0} y={top + 46} textAnchor="middle" fill="#f5f5f7" style={{ font: '600 16px var(--cw-font-display)', letterSpacing: '-0.01em' }}>{info.title}</text>
        <text x={0} y={top + 70} textAnchor="middle" fill={info.col} style={{ font: '500 19px var(--cw-font-mono)' }}>{info.measure}</text>
      </g>
    );
  }

  return (
    <svg
      viewBox="0 0 1440 880"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: 'visible', display: 'block' }}
    >
      {tileEls}
      {/* safe path */}
      <g style={{ pointerEvents: 'none' }}>
        <path d={DPATH} fill="none" stroke={SAFE} strokeWidth={11} strokeLinecap="round" strokeLinejoin="round" opacity={0.12} />
        <path className="cw-path" d={DPATH} fill="none" stroke={SAFE} strokeWidth={3} strokeDasharray="2 12" strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
      </g>
      {/* cane walking the path */}
      <g className="cw-cane" style={{ offsetPath: `path('${DPATH}')`, offsetRotate: '0deg', pointerEvents: 'none' }}>
        <circle className="cw-ring" cx={0} cy={0} r={11} fill="none" stroke={SAFE} strokeWidth={2} />
        <circle className="cw-ring cw-ring-d" cx={0} cy={0} r={11} fill="none" stroke={SAFE} strokeWidth={2} />
        <ellipse cx={0} cy={0} rx={10} ry={5.5} fill={SAFE} opacity={0.22} />
        <line x1={0} y1={0} x2={-34} y2={-106} stroke="#f5f5f7" strokeWidth={5} strokeLinecap="round" />
        <line x1={-3} y1={-10} x2={-7} y2={-24} stroke="#ef4444" strokeWidth={5} strokeLinecap="round" />
        <circle cx={-34} cy={-106} r={5} fill="#f5f5f7" />
        <circle cx={0} cy={0} r={4} fill={SAFE} />
      </g>
      {tip}
    </svg>
  );
};

/* ── Ambient glows (the scene's atmospheric overlays) ─────────── */
const AmbientGlow = () => (
  <>
    <div className="cw-haze" style={{ position: 'absolute', right: '6%', top: '34%', width: 780, height: 780, transform: 'translateY(-50%)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.16), rgba(59,130,246,0) 62%)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', left: 0, top: 0, width: '62%', height: '100%', background: 'linear-gradient(90deg, var(--cw-bg) 32%, rgba(10,10,10,0.72) 64%, rgba(10,10,10,0))', pointerEvents: 'none', zIndex: 2 }} />
    <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 90% at 50% 8%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55))', pointerEvents: 'none', zIndex: 2 }} />
  </>
);

/* ── Shared chrome pieces ─────────────────────────────────────── */
const LivePill = () => (
  <div className="cw-pill"><span className="cw-pill-dot" />Live detection model</div>
);

const Legend = () => (
  <div className="cw-glass" style={{ display: 'flex', alignItems: 'center', gap: 22, padding: '14px 20px', border: '1px solid var(--cw-line)', borderRadius: 'var(--cw-r-full)', flexWrap: 'wrap' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><span style={{ width: 13, height: 13, borderRadius: 3, background: '#33506f' }} /><span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-muted)' }}>Trip · ≤5cm</span></div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><span style={{ width: 13, height: 13, borderRadius: 3, background: '#2f63a8' }} /><span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-muted)' }}>Mid · ≤1m</span></div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><span style={{ width: 13, height: 13, borderRadius: 3, background: 'var(--cw-accent)', boxShadow: '0 0 10px var(--cw-accent-20)' }} /><span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-muted)' }}>High · &gt;1m</span></div>
    <div style={{ width: 1, height: 18, background: 'var(--cw-line-2)' }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><span style={{ width: 18, height: 3, borderRadius: 2, background: SAFE, boxShadow: `0 0 8px ${SAFE}` }} /><span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-muted)' }}>Safe path</span></div>
  </div>
);

const ReplayButton = ({ onClick }) => (
  <button onClick={onClick} className="cw-replay" type="button">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
    Replay scan
  </button>
);

const EYEBROW = 'Obstacle intelligence';
const HEADLINE = 'Know the ground before you reach it.';
const SUBCOPY = 'ClearWay maps the pavement ahead and underfoot — lifting every obstacle by its height, so the safe path is the obvious one.';

const CTAButtons = () => (
  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
    <Link to="/technology" className="cw-btn cw-btn-primary">See the detection logic</Link>
    <Link to="/contact" className="cw-btn cw-btn-ghost">Talk to our team</Link>
  </div>
);

/* ── Main component ───────────────────────────────────────────── */
const HeroDetectionMap = () => {
  const reducedRef = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );

  const [elapsed, setElapsed] = useState(reducedRef.current ? 999999 : 0);
  const [hover, setHover] = useState(null);
  const [isWide, setIsWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  );
  const [scale, setScale] = useState(() => {
    if (typeof window === 'undefined') return 1;
    return Math.min(1, Math.min(window.innerWidth - 48, 1280) / 1440);
  });

  const rafRef = useRef(null);
  const t0Ref = useRef(0);
  const frameRef = useRef(null);
  const sectionRef = useRef(null);
  const playedRef = useRef(false);

  const play = useCallback(() => {
    if (reducedRef.current) { setElapsed(999999); return; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    t0Ref.current = performance.now();
    const step = (now) => {
      const e = now - t0Ref.current;
      setElapsed(e);
      if (e < 2700) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  const replay = useCallback(() => {
    if (reducedRef.current) { setElapsed(999999); return; }
    play();
  }, [play]);

  /* Start the scan the first time the scene scrolls into view. */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    if (reducedRef.current) { setElapsed(999999); return undefined; }
    if (typeof IntersectionObserver === 'undefined') { play(); playedRef.current = true; return undefined; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            play();
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [play]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  /* Track width: choose layout + scale the desktop stage to fit. */
  useEffect(() => {
    const measure = () => {
      const wide = window.innerWidth >= 1024;
      setIsWide(wide);
      const w = frameRef.current ? frameRef.current.clientWidth : 0;
      if (w > 0) setScale(Math.min(1, w / 1440));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && frameRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(frameRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [isWide]);

  return (
    <section
      ref={sectionRef}
      data-testid="detection-map-section"
      className="cw-detect relative py-24 px-6 overflow-hidden bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto">

        {/* Section intro */}
        <div className="max-w-3xl mb-12 lg:hidden">
          <span className="cw-eyebrow block mb-4">{EYEBROW}</span>
          <h2 className="cw-gradient-text" style={{ fontFamily: 'var(--cw-font-display)', fontWeight: 800, fontSize: 'clamp(2rem, 8vw, 3rem)', lineHeight: 1.04, letterSpacing: 'var(--cw-track-tightest)', marginBottom: 16, textWrap: 'balance' }}>{HEADLINE}</h2>
          <p style={{ fontSize: 'var(--cw-text-lg)', lineHeight: 1.6, color: 'var(--cw-ink-muted)', marginBottom: 28 }}>{SUBCOPY}</p>
          <CTAButtons />
        </div>

        {/* ── Desktop: the faithful 1440×880 composition, scaled ── */}
        {isWide && (
        <div ref={frameRef} className="cw-frame" style={{ height: 880 * scale }}>
          <div className="cw-stage" style={{ transform: `scale(${scale})` }}>
            <AmbientGlow />

            {/* isometric scene */}
            <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
              <DetectionScene elapsed={elapsed} hover={hover} setHover={setHover} />
            </div>

            {/* live pill (top-right) */}
            <div style={{ position: 'absolute', right: 56, top: 32, zIndex: 5 }}>
              <LivePill />
            </div>

            {/* editorial text column */}
            <div style={{ position: 'absolute', left: 56, top: '50%', transform: 'translateY(-50%)', maxWidth: 486, zIndex: 4, pointerEvents: 'none' }}>
              <span className="cw-eyebrow" style={{ display: 'block', marginBottom: 20 }}>{EYEBROW}</span>
              <h2 className="cw-gradient-text" style={{ fontFamily: 'var(--cw-font-display)', fontWeight: 800, fontSize: 60, lineHeight: 1.02, letterSpacing: 'var(--cw-track-tightest)', marginBottom: 24, textWrap: 'balance' }}>{HEADLINE}</h2>
              <p style={{ fontSize: 'var(--cw-text-lg)', lineHeight: 1.6, color: 'var(--cw-ink-muted)', maxWidth: 432, marginBottom: 36, textWrap: 'pretty' }}>{SUBCOPY}</p>
              <div style={{ pointerEvents: 'auto' }}><CTAButtons /></div>
            </div>

            {/* legend (bottom-left) */}
            <div style={{ position: 'absolute', left: 56, bottom: 40, zIndex: 4, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-dim)', letterSpacing: 'var(--cw-track-wide)' }}>Hover any tile to inspect what the sensors flagged.</span>
              <Legend />
            </div>

            {/* replay (bottom-right) */}
            <div style={{ position: 'absolute', right: 48, bottom: 44, zIndex: 5 }}>
              <ReplayButton onClick={replay} />
            </div>
          </div>
        </div>
        )}

        {/* ── Mobile / tablet: identical scene, stacked ── */}
        {!isWide && (
          <>
            <div className="cw-scene-card">
              <AmbientGlow />
              <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                <DetectionScene elapsed={elapsed} hover={hover} setHover={setHover} />
              </div>
              <div style={{ position: 'absolute', right: 16, top: 16, zIndex: 5 }}>
                <LivePill />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-4">
              <span style={{ fontSize: 'var(--cw-text-xs)', color: 'var(--cw-ink-dim)', letterSpacing: 'var(--cw-track-wide)' }}>Tap any tile to inspect what the sensors flagged.</span>
              <div style={{ overflowX: 'auto' }}><Legend /></div>
              <div><ReplayButton onClick={replay} /></div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HeroDetectionMap;
